// Agents cite code as Markdown links whose destination is a path rather than a
// URL — Codex writes `[AppModel.swift:2172](/abs/worktree/…/AppModel.swift:2172)`,
// others use GitHub's `#L12-L20`. Those open in the workspace file viewer instead
// of the browser. Mirrored by `FileLink` in the Swift client.

import { isBareFileReference } from "./markdown";

export interface FileLinkTarget {
  /** The path as written: absolute on the worker, or relative to the worktree. */
  path: string;
  /** 1-based first line, or null for the whole file. */
  line: number | null;
  /** 1-based last line, inclusive; equals `line` for a single line. */
  endLine: number | null;
}

const FRAGMENT_LINES_RE = /#L(\d+)(?:C\d+)?(?:-L?(\d+)(?:C\d+)?)?$/i;
const SUFFIX_LINES_RE = /:(\d+)(?::\d+)?(?:-(\d+)(?::\d+)?)?$/;

/** Reads a link destination as a file reference, or null when it is a URL. */
export function parseFileLink(href: string): FileLinkTarget | null {
  let value = href.trim();
  // Any scheme (https:, mailto:) or a protocol-relative URL is a real link.
  if (!value || value.startsWith("//") || value.startsWith("#")) return null;
  if (!isBareFileReference(value) && /^[a-z][a-z0-9+.-]*:/i.test(value)) return null;
  value = value.replace(/\?[^#]*/, "");

  let line: number | null = null;
  let endLine: number | null = null;
  const lines = FRAGMENT_LINES_RE.exec(value) ?? SUFFIX_LINES_RE.exec(value);
  if (lines) {
    line = Number(lines[1]);
    endLine = lines[2] ? Number(lines[2]) : line;
    value = value.slice(0, lines.index);
  }
  value = value.replace(/#.*$/, "");

  try {
    value = decodeURIComponent(value);
  } catch {
    // A stray `%` is just part of the name.
  }
  value = value.replace(/^(\.\/)+/, "");
  if (!value || value.endsWith("/")) return null;
  if (line !== null && line < 1) line = endLine = null;
  if (line !== null && (endLine === null || endLine < line)) endLine = line;
  return { path: value, line, endLine };
}

/**
 * Maps a linked path onto a path in the worktree. The client never learns where
 * the worktree lives on the worker, so an absolute path is matched by its
 * longest tail that is a known file, or by its `.context` tail, and returns
 * null when nothing matches.
 *
 * A relative path that matches nothing is trusted as written, exactly as it is
 * before the file list arrives: the list names tracked and untracked files, not
 * ignored ones, so it is not a complete index of the worktree and cannot be the
 * authority on whether a path exists. Opening it reports a missing file if the
 * link was wrong.
 */
export function resolveWorkspacePath(path: string, knownPaths: readonly string[] | null): string | null {
  const absolute = path.startsWith("/");
  if (!knownPaths) return absolute ? contextPath(path) : path;
  if (!absolute && knownPaths.includes(path)) return path;
  let best: string | null = null;
  for (const candidate of knownPaths) {
    // An absolute path ends with the worktree path; a relative one written from
    // a subdirectory is the tail of it.
    const matches = absolute ? path.endsWith(`/${candidate}`) : candidate.endsWith(`/${path}`);
    if (!matches) continue;
    if (!best || (absolute ? candidate.length > best.length : candidate.length < best.length)) best = candidate;
  }
  if (best) return best;
  return absolute ? contextPath(path) : path;
}

/** The workspace's `.context` directory is ignored, so the file list never
 * names what an agent saves there — screenshots, generated images. It sits at
 * the worktree root on every worker, which is enough to place a path into it. */
function contextPath(path: string): string | null {
  const index = path.indexOf("/.context/");
  return index >= 0 && index + "/.context/".length < path.length ? path.slice(index + 1) : null;
}
